import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { leavesApi, employeesApi, attendanceApi } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, 
  Calendar as CalendarIcon, 
  Check,
  X,
  Clock,
  Filter,
  History,
  Search,
  Users,
  CalendarDays,
  FileText,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import { cn } from '../lib/utils';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const LEAVE_TYPES = [
  { value: 'casual', label: 'Casual Leave', color: 'bg-blue-500' },
  { value: 'sick', label: 'Sick Leave', color: 'bg-red-500' },
  { value: 'earned', label: 'Earned Leave', color: 'bg-green-500' },
  { value: 'maternity', label: 'Maternity Leave', color: 'bg-pink-500' },
  { value: 'paternity', label: 'Paternity Leave', color: 'bg-purple-500' },
  { value: 'unpaid', label: 'Leave Without Pay', color: 'bg-gray-500' }
];

export default function LeavesPage() {
  const { user, canAccessHR, isEmployee } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [historyMonth, setHistoryMonth] = useState(1); // January
  const [historyYear, setHistoryYear] = useState(2026);
  const [searchHistory, setSearchHistory] = useState('');
  const [formData, setFormData] = useState({
    leave_type: 'casual',
    start_date: null,
    end_date: null,
    reason: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus, filterEmployee]);

  useEffect(() => {
    if (canAccessHR) {
      fetchLeaveHistory();
    }
  }, [historyMonth, historyYear, canAccessHR]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') {
        params.status = filterStatus;
      }
      if (filterEmployee && filterEmployee !== 'all') {
        params.employee_id = filterEmployee;
      }

      const [leavesRes] = await Promise.all([
        leavesApi.getAll(params)
      ]);

      setLeaves(leavesRes.data);

      if (canAccessHR) {
        const empRes = await employeesApi.getAll();
        setEmployees(empRes.data);
      }

      // Get leave balance for current user
      if (user?.employee_id) {
        const balanceRes = await leavesApi.getBalance(user.employee_id);
        setLeaveBalance(balanceRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch leaves:', error);
      toast.error('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await attendanceApi.getLeaveHistory({ month: historyMonth, year: historyYear });
      setLeaveHistory(res.data);
    } catch (error) {
      console.error('Failed to fetch leave history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    
    if (!formData.start_date || !formData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    try {
      await leavesApi.apply({
        leave_type: formData.leave_type,
        start_date: format(formData.start_date, 'yyyy-MM-dd'),
        end_date: format(formData.end_date, 'yyyy-MM-dd'),
        reason: formData.reason
      });
      toast.success('Leave application submitted successfully');
      setShowApplyModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply leave');
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      await leavesApi.approve(leaveId);
      toast.success('Leave approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve leave');
    }
  };

  const handleReject = async (leaveId) => {
    try {
      await leavesApi.reject(leaveId);
      toast.success('Leave rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject leave');
    }
  };

  const resetForm = () => {
    setFormData({
      leave_type: 'casual',
      start_date: null,
      end_date: null,
      reason: ''
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="badge-warning">Pending</Badge>;
      case 'approved':
        return <Badge className="badge-success">Approved</Badge>;
      case 'rejected':
        return <Badge className="badge-error">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const calculateDays = () => {
    if (!formData.start_date || !formData.end_date) return 0;
    return differenceInDays(formData.end_date, formData.start_date) + 1;
  };

  const pendingCount = leaves.filter(l => l.status === 'pending').length;
  
  // Filter leave history by search
  const filteredLeaveHistory = leaveHistory.filter(record => 
    !searchHistory || 
    record.employee_name?.toLowerCase().includes(searchHistory.toLowerCase()) ||
    record.emp_code?.toLowerCase().includes(searchHistory.toLowerCase())
  );

  // Group leave history by employee for summary
  const employeeLeaveSummary = filteredLeaveHistory.reduce((acc, record) => {
    const key = record.employee_name || 'Unknown';
    if (!acc[key]) {
      acc[key] = { name: key, emp_code: record.emp_code, count: 0, dates: [] };
    }
    acc[key].count++;
    acc[key].dates.push(record.date);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="leaves-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Leave Management</h1>
          <p className="page-description">Apply for leave, track requests, and view history</p>
        </div>
        <Button 
          className="btn-primary gap-2"
          onClick={() => setShowApplyModal(true)}
          data-testid="apply-leave-btn"
        >
          <Plus className="h-4 w-4" />
          Apply Leave
        </Button>
      </div>

      {/* Leave Balance Cards */}
      {leaveBalance && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Casual Leave</p>
                  <p className="text-2xl font-bold">
                    {leaveBalance.casual.total - leaveBalance.casual.used} / {leaveBalance.casual.total}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {leaveBalance.casual.used} used
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <CalendarDays className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                <div 
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${((leaveBalance.casual.total - leaveBalance.casual.used) / leaveBalance.casual.total) * 100}%` }}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Sick Leave</p>
                  <p className="text-2xl font-bold">
                    {leaveBalance.sick.total - leaveBalance.sick.used} / {leaveBalance.sick.total}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {leaveBalance.sick.used} used
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <CalendarDays className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                <div 
                  className="h-full bg-red-500 rounded-full transition-all duration-500"
                  style={{ width: `${((leaveBalance.sick.total - leaveBalance.sick.used) / leaveBalance.sick.total) * 100}%` }}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Earned Leave</p>
                  <p className="text-2xl font-bold">
                    {leaveBalance.earned.total - leaveBalance.earned.used} / {leaveBalance.earned.total}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {leaveBalance.earned.used} used
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CalendarDays className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
                <div 
                  className="h-full bg-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${((leaveBalance.earned.total - leaveBalance.earned.used) / leaveBalance.earned.total) * 100}%` }}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs for HR */}
      {canAccessHR ? (
        <Tabs defaultValue="pending" className="space-y-4">
          <TabsList className="grid w-full md:w-auto grid-cols-3 md:inline-flex">
            <TabsTrigger value="pending" className="relative gap-2">
              <Clock className="h-4 w-4" />
              Pending
              {pendingCount > 0 && (
                <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-[#1E88E5] text-white">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" className="gap-2">
              <FileText className="h-4 w-4" />
              All Requests
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="h-4 w-4" />
              Attendance History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-500" />
                  Pending Leave Requests
                </CardTitle>
                <CardDescription>Review and approve leave requests from employees</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <LeaveTable 
                  leaves={leaves.filter(l => l.status === 'pending')} 
                  loading={loading}
                  canAccessHR={canAccessHR}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  getStatusBadge={getStatusBadge}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="all">
            <Card>
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full sm:w-48">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                    <SelectTrigger className="w-full sm:w-64">
                      <SelectValue placeholder="All Employees" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Employees</SelectItem>
                      {employees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
              <CardContent className="p-0 pt-0">
                <LeaveTable 
                  leaves={leaves} 
                  loading={loading}
                  canAccessHR={canAccessHR}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  getStatusBadge={getStatusBadge}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <History className="h-5 w-5 text-[#1E88E5]" />
                      Leave Records from Attendance
                    </CardTitle>
                    <CardDescription>
                      Employees marked as "On Leave" in attendance records for {MONTHS.find(m => m.value === historyMonth)?.label} {historyYear}
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Select value={historyMonth.toString()} onValueChange={(v) => setHistoryMonth(parseInt(v))}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map(m => (
                          <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={historyYear.toString()} onValueChange={(v) => setHistoryYear(parseInt(v))}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026].map(y => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <p className="text-2xl font-bold text-amber-700">{filteredLeaveHistory.length}</p>
                    <p className="text-sm text-amber-600">Total Leave Days</p>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-2xl font-bold text-blue-700">{Object.keys(employeeLeaveSummary).length}</p>
                    <p className="text-sm text-blue-600">Employees on Leave</p>
                  </div>
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <p className="text-2xl font-bold text-purple-700">
                      {filteredLeaveHistory.length > 0 ? (filteredLeaveHistory.length / Object.keys(employeeLeaveSummary).length).toFixed(1) : 0}
                    </p>
                    <p className="text-sm text-purple-600">Avg Days/Employee</p>
                  </div>
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <p className="text-2xl font-bold text-green-700">
                      {Math.max(...Object.values(employeeLeaveSummary).map(e => e.count), 0)}
                    </p>
                    <p className="text-sm text-green-600">Max Leave Days</p>
                  </div>
                </div>

                {/* Search */}
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or code..."
                    value={searchHistory}
                    onChange={(e) => setSearchHistory(e.target.value)}
                    className="pl-9"
                  />
                </div>

                {/* Leave History Table */}
                <div className="responsive-table-container">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="font-semibold">Date</TableHead>
                        <TableHead className="font-semibold">Employee Code</TableHead>
                        <TableHead className="font-semibold">Employee Name</TableHead>
                        <TableHead className="font-semibold">Day</TableHead>
                        <TableHead className="font-semibold">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyLoading ? (
                        [...Array(5)].map((_, i) => (
                          <TableRow key={i}>
                            {[...Array(5)].map((_, j) => (
                              <TableCell key={j}>
                                <div className="h-4 bg-muted rounded animate-pulse" />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      ) : filteredLeaveHistory.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-12">
                            <div className="flex flex-col items-center gap-3">
                              <CalendarDays className="h-12 w-12 text-muted-foreground/50" />
                              <p className="font-medium text-muted-foreground">No leave records found</p>
                              <p className="text-sm text-muted-foreground/75">
                                No employees were marked on leave for this period
                              </p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredLeaveHistory.map((record, idx) => {
                          const recordDate = new Date(record.date);
                          return (
                            <TableRow key={idx} className="hover:bg-muted/30 transition-colors">
                              <TableCell>
                                {recordDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </TableCell>
                              <TableCell className="font-mono text-sm">{record.emp_code || 'N/A'}</TableCell>
                              <TableCell className="font-medium">{record.employee_name}</TableCell>
                              <TableCell className="text-muted-foreground">
                                {recordDate.toLocaleDateString('en-IN', { weekday: 'short' })}
                              </TableCell>
                              <TableCell>
                                <Badge className="bg-amber-100 text-amber-700 border-amber-200">On Leave</Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Employee-wise Summary */}
                {Object.keys(employeeLeaveSummary).length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Employee-wise Summary
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {Object.values(employeeLeaveSummary)
                        .sort((a, b) => b.count - a.count)
                        .slice(0, 9)
                        .map((emp, idx) => (
                          <div 
                            key={idx} 
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border hover:bg-muted/50 transition-colors"
                          >
                            <div>
                              <p className="font-medium text-sm">{emp.name}</p>
                              <p className="text-xs text-muted-foreground">{emp.emp_code}</p>
                            </div>
                            <Badge variant="secondary" className="font-mono">
                              {emp.count} {emp.count === 1 ? 'day' : 'days'}
                            </Badge>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>My Leave Requests</CardTitle>
            <CardDescription>View your leave application history</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <LeaveTable 
              leaves={leaves} 
              loading={loading}
              canAccessHR={false}
              getStatusBadge={getStatusBadge}
            />
          </CardContent>
        </Card>
      )}

      {/* Apply Leave Modal */}
      <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-[#1E88E5]" />
              Apply for Leave
            </DialogTitle>
            <DialogDescription>Submit a new leave request for approval</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleApplyLeave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="leave_type">Leave Type</Label>
              <Select 
                value={formData.leave_type} 
                onValueChange={(v) => setFormData({ ...formData, leave_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <div className={cn("h-2 w-2 rounded-full", type.color)} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.start_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.start_date ? format(formData.start_date, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.start_date}
                      onSelect={(date) => setFormData({ ...formData, start_date: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.end_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.end_date ? format(formData.end_date, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.end_date}
                      onSelect={(date) => setFormData({ ...formData, end_date: date })}
                      initialFocus
                      disabled={(date) => formData.start_date && date < formData.start_date}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {formData.start_date && formData.end_date && (
              <div className="p-4 rounded-lg bg-gradient-to-r from-[#1E88E5]/10 to-[#42A5F5]/10 text-center border border-[#1E88E5]/20">
                <span className="text-3xl font-bold text-[#1E88E5]">{calculateDays()}</span>
                <span className="text-muted-foreground ml-2">day(s)</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="reason">Reason</Label>
              <Textarea
                id="reason"
                placeholder="Please provide a reason for your leave request..."
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                required
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowApplyModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Submit Request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Leave Table Component
function LeaveTable({ leaves, loading, canAccessHR, onApprove, onReject, getStatusBadge }) {
  return (
    <div className="responsive-table-container">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="font-semibold">Employee</TableHead>
            <TableHead className="font-semibold">Leave Type</TableHead>
            <TableHead className="font-semibold">From</TableHead>
            <TableHead className="font-semibold">To</TableHead>
            <TableHead className="font-semibold">Days</TableHead>
            <TableHead className="font-semibold">Reason</TableHead>
            <TableHead className="font-semibold">Status</TableHead>
            {canAccessHR && <TableHead className="text-right font-semibold">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            [...Array(5)].map((_, i) => (
              <TableRow key={i}>
                {[...Array(canAccessHR ? 8 : 7)].map((_, j) => (
                  <TableCell key={j}>
                    <div className="h-4 bg-muted rounded animate-pulse" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : leaves.length === 0 ? (
            <TableRow>
              <TableCell colSpan={canAccessHR ? 8 : 7} className="text-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <FileText className="h-12 w-12 text-muted-foreground/50" />
                  <p className="font-medium text-muted-foreground">No leave requests found</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            leaves.map((leave) => (
              <TableRow key={leave.id} className="hover:bg-muted/30 transition-colors">
                <TableCell className="font-medium">{leave.employee_name}</TableCell>
                <TableCell>
                  <span className="capitalize">{leave.leave_type?.replace('_', ' ')}</span>
                </TableCell>
                <TableCell>{new Date(leave.start_date).toLocaleDateString('en-IN')}</TableCell>
                <TableCell>{new Date(leave.end_date).toLocaleDateString('en-IN')}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{leave.days}</Badge>
                </TableCell>
                <TableCell className="max-w-xs truncate">{leave.reason}</TableCell>
                <TableCell>{getStatusBadge(leave.status)}</TableCell>
                {canAccessHR && (
                  <TableCell className="text-right">
                    {leave.status === 'pending' && (
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          size="sm" 
                          className="btn-primary gap-1 h-8"
                          onClick={() => onApprove(leave.id)}
                          data-testid={`approve-${leave.id}`}
                        >
                          <Check className="h-4 w-4" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          className="gap-1 h-8"
                          onClick={() => onReject(leave.id)}
                          data-testid={`reject-${leave.id}`}
                        >
                          <X className="h-4 w-4" />
                          Reject
                        </Button>
                      </div>
                    )}
                    {leave.status !== 'pending' && leave.approved_by && (
                      <span className="text-sm text-muted-foreground">
                        by {leave.approved_by}
                      </span>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
